/**
 * The grocery guided tour — CONFIGURATION ONLY.
 *
 * There is no tour code in here and no tour code in the grocery pages: the
 * engine is the shared `components/shared/GuidedTour.jsx` used by the Command
 * Center. A step is an anchor (a real control on the real screen) plus an i18n
 * key. Grocery wording only — no buyer, sales, menu, guest or resident.
 *
 * ADDING A STEP LATER IS CONFIG, NOT CODE: give it a `flag`, and it appears as
 * soon as that feature flag is true for the organization (see FUTURE_STEPS).
 */
export const TOUR_VERSION = 'grocery.v1';

// The mascot: the same chef hat the customer already knows from the chat.
export const TOUR_MASCOT = '/static/icons/grocery/chef-hat-avatar.svg';

// The bubble must never sit on top of these.
export const TOUR_AVOID = ['chef-hat-btn', 'grocery-points-chip', 'grocery-added-toast',
                           'grocery-open-add-flow', 'grocery-item-add',
                           'grocery-favourite-confirm'];

const step = (id, key, anchors, extra = {}) => ({
    id, anchors, titleKey: `groceryTour.${key}`, ...extra,
});

// Eight steps for an adult. One action each, one line each.
export const ADULT_STEPS = [
    step('welcome', 'adult.welcome', ['grocery-household-name'], { nextKey: 'groceryTour.letsGo' }),
    step('add', 'adult.add', ['grocery-open-add-flow']),
    // This one does NOT advance on a tap: tapping an aisle tile opens the
    // aisle, so the tour waits until a food really lands on the list (`goTo`).
    step('aisle', 'adult.aisle', ['grocery-aisle-tile-fruit', 'grocery-aisle-item-bananas',
                                  'grocery-item-add', 'grocery-add-flow'], { noTap: true }),
    step('tick', 'adult.tick', ['[data-tour-anchor="tick"]', 'grocery-check-mode-btn']),
    step('heart', 'adult.heart', ['[data-tour-anchor="heart"]', 'grocery-item-favourite-heart'],
         { optional: true }),
    step('chef', 'adult.chef', ['chef-hat-btn']),
    step('points', 'adult.points', ['grocery-points-chip']),
    step('ready', 'adult.ready', ['grocery-help-btn']),
];

// Six steps for the kid view: no points, no household, bigger and simpler.
export const KID_STEPS = [
    step('welcome', 'kid.welcome', ['grocery-household-name'], { nextKey: 'groceryTour.letsGo' }),
    step('add', 'kid.add', ['grocery-open-add-flow']),
    step('tick', 'kid.tick', ['[data-tour-anchor="tick"]', 'grocery-check-mode-btn']),
    step('heart', 'kid.heart', ['[data-tour-anchor="heart"]', 'grocery-item-favourite-heart'],
         { optional: true }),
    step('chef', 'kid.chef', ['chef-hat-btn']),
    step('ready', 'kid.ready', ['grocery-help-btn']),
];

// Three steps for "Send your list" — one action and one line each, exactly
// like the big tour, and never more than three.
export const SEND_STEPS = [
    step('send_open', 'send.open', ['grocery-send-list-btn'], { noTap: true }),
    step('send_window', 'send.window', ['grocery-windows', 'grocery-send-sheet'], { noTap: true }),
    step('send_confirm', 'send.confirm', ['grocery-send-confirm', 'grocery-pin-input',
                                         'grocery-send-done'], { noTap: true }),
];

/**
 * Steps for features that DO NOT EXIST YET. Nothing here is built, nothing is
 * rendered: each entry waits on a feature flag from the server. When delivery
 * (or approvals, or the grocer console) ships, the only change is turning the
 * flag on and writing the one line in en/fr/es — no component is edited.
 */
export const FUTURE_STEPS = {
    delivery: {
        personas: ['adult'],
        after: 'points',
        step: step('delivery', 'adult.delivery', ['grocery-delivery-btn'], { flag: 'delivery' }),
    },
    approvals: {
        personas: ['adult'],
        after: 'points',
        step: step('approvals', 'adult.approvals', ['grocery-approve-btn'], { flag: 'approvals' }),
    },
    /**
     * THE "SEND YOUR LIST" MINI-TOUR — three steps, its OWN tour.
     *
     * It is NOT spliced into the adult eight: the owner's rule is that the
     * adult tour never grows past eight steps. It is declared with the rest of
     * the future config as `standalone`, so nothing had to change in the
     * engine or in the pages to add it — the send sheet asks for SEND_STEPS
     * and the same shared engine draws it.
     */
    sendList: {
        standalone: true,
        personas: ['adult'],
        steps: SEND_STEPS,
    },
};

/** The step list for one persona, with any flagged future step spliced in. */
export function groceryTourSteps({ kid = false, flags = {} } = {}) {
    const base = (kid ? KID_STEPS : ADULT_STEPS).slice();
    Object.entries(FUTURE_STEPS).forEach(([name, def]) => {
        if (def.standalone) return;              // its own tour, never spliced in
        if (!flags[name]) return;
        if (!def.personas.includes(kid ? 'kid' : 'adult')) return;
        const at = base.findIndex((s) => s.id === def.after);
        base.splice(at < 0 ? base.length - 1 : at + 1, 0, def.step);
    });
    return base;
}

export const TOUR_LABEL_KEYS = {
    back: 'groceryTour.back',
    next: 'groceryTour.next',
    done: 'groceryTour.done',
    skip: 'groceryTour.skip',
    close: 'groceryTour.close',
};
